import type { OHLCV } from '@shared/types/candle'

import { registerIndicator } from '@/indicators/registry'
import type { IndicatorConfig, IndicatorResult } from '@/types/indicator'
import { IndicatorBase } from '@/types/indicator'

const CONFIG: IndicatorConfig = {
  id: 'cmf',
  name: 'Chaikin Money Flow',
  shortName: 'CMF',
  category: 'volume',
  overlay: false,
  params: [{ key: 'period', label: 'Period', type: 'number', default: 20, min: 2, max: 200 }],
  lines: [{ key: 'cmf', label: 'CMF', color: '#00bcd4', width: 1 }],
  // Not using priceRange (-1 to 1) — values rarely reach extremes, auto-scale is more informative
  priceLevels: [0], // zero line
}

class CMF extends IndicatorBase {
  readonly config = CONFIG

  calculate(candles: OHLCV[], params: Record<string, number | string | boolean>): IndicatorResult {
    const period = Math.max(2, Number(params['period']) || 20)
    const n = candles.length
    const values: (number | null)[] = new Array<number | null>(n).fill(null)

    if (n < period) return { lines: [{ key: 'cmf', values }] }

    // Money Flow Multiplier = (2C - L - H) / (H - L)
    // Money Flow Volume = MFM × Volume
    // CMF(period) = sum(MFV, period) / sum(Volume, period)
    const mfv: number[] = candles.map((c) => {
      const range = c.h - c.l
      if (range === 0) return 0
      return ((2 * c.c - c.l - c.h) / range) * c.v
    })

    for (let i = period - 1; i < n; i++) {
      let sumMFV = 0
      let sumVol = 0
      for (let j = i - period + 1; j <= i; j++) {
        sumMFV += mfv[j]
        sumVol += candles[j].v
      }
      values[i] = sumVol === 0 ? 0 : sumMFV / sumVol
    }

    return { lines: [{ key: 'cmf', values }] }
  }
}

registerIndicator(CMF, CONFIG)
